/**
 * The pet, for whoever is about to handle it.
 *
 * A vet, trainer or walker opening a booking was told the animal's name and species and
 * nothing else — not its breed, not its weight, not that it is severely allergic to
 * chicken. This card carries the facts that matter while the animal is in someone's care.
 *
 * Two rules shape it:
 *
 * 1. Safety first, literally. Allergies and active conditions sit above the pleasantries,
 *    in warning tones, because they are the reason this card exists.
 * 2. Silence is never read as "none". An owner who never filled in the allergy list and an
 *    owner who confirmed there are none are different situations, and a vendor who cannot
 *    tell them apart will assume the reassuring one. Every empty section says "none
 *    recorded" and says who would have recorded it.
 *
 * The clinical file — past visits, prescriptions, medical records — is deliberately NOT
 * here. That belongs to the vet relationship, not to every booking.
 */
import { AlertTriangle, PawPrint, Syringe } from 'lucide-react';
import { AuthImage } from '@/components/auth-image';
import type { BookingPetInfo } from '@/features/booking/types';

/** "2 yr 3 mo", "7 mo" — months alone stop being legible somewhere around a year. */
function age_label(months: number | null | undefined): string | null {
  if (months == null) return null;
  if (months < 1) return 'under a month';
  if (months < 24) return `${months} mo`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest === 0 ? `${years} yr` : `${years} yr ${rest} mo`;
}

/** `NOT_NEUTERED` -> `Not neutered`. The raw enum was leaking onto the card. */
function title_case(v: string): string {
  const words = v.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const SEVERITY_TONE: Record<string, string> = {
  SEVERE: 'border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.12)] text-destructive',
  MODERATE: 'border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.18)] text-warning',
  MILD: 'border-border bg-muted text-muted-foreground',
};

function date_label(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function humanize(v: string): string {
  return v.replace(/_/g, ' ').toLowerCase();
}

export function BookingPetCard({ pet }: { pet: BookingPetInfo }) {
  const allergies = pet.allergies ?? [];
  const conditions = pet.conditions ?? [];
  const vaccinations = pet.vaccinations ?? [];

  /*
   * Only the detail endpoints load the health relations. Handed a list row, this card would
   * otherwise show three confident "none recorded" panels off the back of data that was
   * never fetched — so it falls back to the identity line alone.
   */
  const has_health_data = pet.allergies !== undefined;

  const now = Date.now();
  const overdue = vaccinations.filter((v) => v.next_due_on && new Date(v.next_due_on).getTime() < now);
  const latest = vaccinations.slice(0, 3);

  const identity = [
    pet.breed,
    age_label(pet.age_months),
    pet.sex && pet.sex !== 'UNKNOWN' ? title_case(pet.sex) : null,
    pet.neuter_status && pet.neuter_status !== 'UNKNOWN' ? title_case(pet.neuter_status) : null,
    pet.weight_kg ? `${pet.weight_kg} kg` : null,
  ].filter(Boolean);

  return (
    <div className="rounded-input border border-border p-3" data-testid="booking-pet-card">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <PawPrint className="h-3.5 w-3.5" /> Pet
      </p>

      <div className="flex items-start gap-3">
        {pet.photo_url ? (
          /*
             Not a plain <img>. The photo lives behind an authenticated API route — a bare
             src rendered a broken-image icon next to the animal's name, because the
             browser sends no token. AuthImage fetches it as a blob with the session and
             degrades to a placeholder rather than a broken glyph.
          */
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-input bg-muted">
            <AuthImage
              path={pet.photo_url}
              alt={pet.name}
              className="h-full w-full object-cover"
              data-testid="pet-photo"
            />
          </div>
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-input bg-muted">
            <PawPrint className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold" data-testid="pet-name">
            {pet.name} <span className="font-normal text-muted-foreground">· {pet.species.toLowerCase()}</span>
          </p>
          {identity.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="pet-identity">
              {identity.join(' · ')}
            </p>
          )}
          {pet.color_markings && <p className="text-xs text-muted-foreground">{pet.color_markings}</p>}
          {/* A pet that bolts is found by its chip, so the number travels with the leash. */}
          {pet.microchip_id && (
            <p className="text-xs text-muted-foreground" data-testid="pet-microchip">
              Microchip {pet.microchip_id}
            </p>
          )}
        </div>
      </div>

      {has_health_data && (
        <div className="mt-3 space-y-2">
          <section data-testid="pet-allergies">
            <p className="text-xs font-semibold text-foreground">Allergies</p>
            {allergies.length === 0 ? (
              <p className="text-xs text-muted-foreground">None recorded by the owner.</p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {allergies.map((a) => (
                  <span
                    key={a.allergen}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
                      SEVERITY_TONE[a.severity] ?? SEVERITY_TONE.MILD
                    }`}
                  >
                    {a.severity === 'SEVERE' && <AlertTriangle className="h-3 w-3" />}
                    {a.allergen}
                    <span className="opacity-70">· {a.severity.toLowerCase()}</span>
                  </span>
                ))}
              </div>
            )}
          </section>

          <section data-testid="pet-conditions">
            <p className="text-xs font-semibold text-foreground">Ongoing conditions</p>
            {conditions.length === 0 ? (
              /*
                 Not "none recorded by the owner" — resolved conditions are filtered out
                 server-side, so this panel is empty both when nothing was ever recorded and
                 when everything recorded has since resolved. The wording has to cover both
                 without claiming the animal has no history.
              */
              <p className="text-xs text-muted-foreground">No ongoing conditions recorded.</p>
            ) : (
              <ul className="mt-0.5 space-y-0.5">
                {conditions.map((c) => (
                  <li key={c.condition} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{c.condition}</span>
                    {' · '}
                    {humanize(c.status)}
                    {c.diagnosed_on && ` · since ${date_label(c.diagnosed_on)}`}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section data-testid="pet-vaccinations">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
              <Syringe className="h-3 w-3" /> Vaccinations
            </p>
            {vaccinations.length === 0 ? (
              <p className="text-xs text-muted-foreground">None recorded by the owner.</p>
            ) : (
              <>
                {overdue.length > 0 && (
                  <p
                    className="mt-1 rounded-control bg-[hsl(var(--warning)/0.18)] px-2 py-1 text-xs font-medium text-warning"
                    data-testid="pet-vaccination-overdue"
                  >
                    {overdue.length === 1 ? '1 vaccination is' : `${overdue.length} vaccinations are`} past due:{' '}
                    {overdue.map((v) => humanize(v.type)).join(', ')}
                  </p>
                )}
                <ul className="mt-0.5 space-y-0.5">
                  {latest.map((v) => (
                    <li key={`${v.type}-${v.administered_on}`} className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{humanize(v.type)}</span>
                      {' · given '}
                      {date_label(v.administered_on)}
                      {v.next_due_on && ` · next ${date_label(v.next_due_on)}`}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {pet.notes && (
            <section data-testid="pet-notes">
              <p className="text-xs font-semibold text-foreground">Owner&rsquo;s notes</p>
              <p className="whitespace-pre-wrap text-xs text-muted-foreground">{pet.notes}</p>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
